import { useEffect, useState } from 'react';

export type ExportFormat = 'pdf' | 'pptx';

export interface ExportOptions {
  format: ExportFormat;
  includeRoadmap: boolean;
}

interface OutputModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions) => void | Promise<void>;
  /** Optional flag from parent: when true, the parent is currently exporting and the modal stays disabled. */
  exporting?: boolean;
}

export function OutputModal({ open, onClose, onExport, exporting }: OutputModalProps) {
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [includeRoadmap, setIncludeRoadmap] = useState(true);

  useEffect(() => {
    if (open) {
      setFormat('pdf');
      setIncludeRoadmap(true);
    }
  }, [open]);

  if (!open) return null;

  const submit = () => {
    void onExport({ format, includeRoadmap });
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 no-print"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-bold text-slate-800">レポート出力</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        <fieldset className="mb-5">
          <legend className="mb-2 text-sm font-medium text-slate-700">出力形式</legend>
          <div className="space-y-2">
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50">
              <input
                type="radio"
                name="format"
                value="pdf"
                checked={format === 'pdf'}
                onChange={() => setFormat('pdf')}
                className="text-brand-accent"
              />
              <span className="text-sm font-medium text-slate-800">PDFで出力</span>
              <span className="ml-auto text-xs text-slate-500">A4縦・自動ページ分割</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50">
              <input
                type="radio"
                name="format"
                value="pptx"
                checked={format === 'pptx'}
                onChange={() => setFormat('pptx')}
                className="text-brand-accent"
              />
              <span className="text-sm font-medium text-slate-800">PPTXで出力</span>
              <span className="ml-auto text-xs text-slate-500">PowerPoint・編集可能</span>
            </label>
          </div>
        </fieldset>

        <fieldset className="mb-6">
          <legend className="mb-2 text-sm font-medium text-slate-700">含める内容</legend>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={includeRoadmap}
              onChange={(e) => setIncludeRoadmap(e.target.checked)}
              className="text-brand-accent"
            />
            <span className="text-sm font-medium text-slate-800">改善ロードマップを含める</span>
          </label>
          <p className="mt-1 text-xs text-slate-500">
            含める場合、出力時はすべてのアクション文が展開された状態になります。
          </p>
        </fieldset>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={exporting}
            className="btn-secondary"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={exporting}
            className="btn-primary"
          >
            {exporting ? '出力中...' : '出力する'}
          </button>
        </div>
      </div>
    </div>
  );
}
